// Lightweight, anonymous analytics client.
// - Generates one session_id per browser session (sessionStorage)
// - Captures UTM params on first hit, persists them in sessionStorage
// - Captures partner referral code (?ref=) once and reuses for the session
// - Fire-and-forget POSTs to /api/analytics/{pageview,event}
import api from "./api";

const SESSION_KEY = "met_session_id";
const UTM_KEY = "met_utm";
const REF_KEY = "met_partner_ref";

function uuid4() {
    // RFC 4122 v4-ish — good enough for an analytics session id
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export function getSessionId() {
    if (typeof window === "undefined") return "";
    try {
        let sid = window.sessionStorage.getItem(SESSION_KEY);
        if (!sid) {
            sid = uuid4();
            window.sessionStorage.setItem(SESSION_KEY, sid);
        }
        return sid;
    } catch {
        return "";
    }
}

export function captureUtmFromUrl() {
    if (typeof window === "undefined") return {};
    try {
        const url = new URL(window.location.href);
        const utm = {
            utm_source: url.searchParams.get("utm_source") || "",
            utm_medium: url.searchParams.get("utm_medium") || "",
            utm_campaign: url.searchParams.get("utm_campaign") || "",
        };
        const ref = (url.searchParams.get("ref") || "").trim().toUpperCase();
        if (ref) window.sessionStorage.setItem(REF_KEY, ref);
        const existing = window.sessionStorage.getItem(UTM_KEY);
        if (existing) return JSON.parse(existing);
        if (utm.utm_source || utm.utm_medium || utm.utm_campaign) {
            window.sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
        }
        return utm;
    } catch {
        return {};
    }
}

export function getStoredUtm() {
    if (typeof window === "undefined") return {};
    try {
        const raw = window.sessionStorage.getItem(UTM_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

export function getStoredPartnerCode() {
    if (typeof window === "undefined") return "";
    try {
        return window.sessionStorage.getItem(REF_KEY) || "";
    } catch {
        return "";
    }
}

function safeReferrer() {
    if (typeof document === "undefined") return "";
    try {
        const ref = document.referrer || "";
        if (ref && typeof window !== "undefined") {
            const refHost = new URL(ref).hostname;
            if (refHost === window.location.hostname) return "";
        }
        return ref;
    } catch {
        return "";
    }
}

export function trackPageview(path) {
    const session_id = getSessionId();
    if (!session_id) return;
    const utm = { ...getStoredUtm(), ...captureUtmFromUrl() };
    const payload = {
        session_id,
        path: path || (typeof window !== "undefined" ? window.location.pathname : ""),
        referrer: safeReferrer(),
        utm_source: utm.utm_source || "",
        utm_medium: utm.utm_medium || "",
        utm_campaign: utm.utm_campaign || "",
    };
    api.post("/analytics/pageview", payload).catch(() => {});
}

// ---------- Live-visitor heartbeat (SalesIQ-style presence) ----------
const VISITOR_KEY = "met_visitor_id";
let heartbeatTimer = null;

function getVisitorId() {
    try {
        let vid = window.localStorage.getItem(VISITOR_KEY);
        if (!vid) {
            vid = uuid4();
            window.localStorage.setItem(VISITOR_KEY, vid);
        }
        return vid;
    } catch {
        return "";
    }
}

function detectBrowser(ua) {
    if (/edg\//i.test(ua)) return "Edge";
    if (/opr\//i.test(ua)) return "Opera";
    if (/chrome|crios/i.test(ua)) return "Chrome";
    if (/firefox|fxios/i.test(ua)) return "Firefox";
    if (/safari/i.test(ua)) return "Safari";
    return "Other";
}

function detectOs(ua) {
    if (/windows/i.test(ua)) return "Windows";
    if (/android/i.test(ua)) return "Android";
    if (/iphone|ipad|ios/i.test(ua)) return "iOS";
    if (/mac os/i.test(ua)) return "macOS";
    if (/linux/i.test(ua)) return "Linux";
    return "Other";
}

export function sendHeartbeat(path) {
    const session_id = getSessionId();
    if (!session_id || typeof window === "undefined") return;
    if (window.location.pathname.startsWith("/admin")) return;
    const ua = window.navigator.userAgent || "";
    const utm = getStoredUtm();
    api.post("/analytics/heartbeat", {
        session_id,
        visitor_id: getVisitorId(),
        path: path || window.location.pathname,
        referrer: safeReferrer(),
        utm_source: utm.utm_source || "",
        utm_medium: utm.utm_medium || "",
        utm_campaign: utm.utm_campaign || "",
        browser: detectBrowser(ua),
        os: detectOs(ua),
        device: /mobile|iphone|android/i.test(ua) ? "Mobile" : "Desktop",
        screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    }).catch(() => {});
}

export function startHeartbeat() {
    if (heartbeatTimer || typeof window === "undefined") return;
    sendHeartbeat();
    heartbeatTimer = setInterval(() => {
        if (document.visibilityState === "visible") sendHeartbeat();
    }, 30000);
}

export function trackEvent(event, extras = {}) {
    const session_id = getSessionId();
    if (!session_id) return;
    const payload = {
        session_id,
        event,
        path: typeof window !== "undefined" ? window.location.pathname : "",
        destination_id: extras.destination_id || "",
        quote_total: extras.quote_total ?? null,
        quote_deposit: extras.quote_deposit ?? null,
    };
    api.post("/analytics/event", payload).catch(() => {});
}

export function getAttributionPayload() {
    return {
        session_id: getSessionId(),
        referrer: safeReferrer(),
        partner_code: getStoredPartnerCode(),
        ...getStoredUtm(),
    };
}
